import React, { useState, useCallback } from 'react';
import { 
  Upload, FileSpreadsheet, Link2, Mail, Users, CheckCircle, 
  AlertCircle, X, Loader2, Download, Send, Trash2, Search
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const BulkEmailImport = ({ onComplete, challengeId, roundId }) => {
  const [activeTab, setActiveTab] = useState('paste'); // paste, csv, sheets
  const [loading, setLoading] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  
  // Form states
  const [pasteEmails, setPasteEmails] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [sheetsUrl, setSheetsUrl] = useState('');
  const [emailColumn, setEmailColumn] = useState('email');
  const [nameColumn, setNameColumn] = useState('name');
  
  const token = localStorage.getItem('company_token');

  const handleParse = async () => {
    setLoading(true);
    try {
      const payload = {
        email_column: emailColumn,
        name_column: nameColumn,
        skip_header: true
      };

      if (activeTab === 'paste' && pasteEmails.trim()) {
        // Parse pasted emails (one per line or comma-separated)
        const emailList = pasteEmails
          .split(/[\n,;]+/)
          .map(e => e.trim())
          .filter(e => e);
        payload.emails = emailList;
      } else if (activeTab === 'csv' && csvFile) {
        // Read CSV file content
        const text = await csvFile.text();
        payload.csv_data = text;
      } else if (activeTab === 'sheets' && sheetsUrl.trim()) {
        payload.google_sheet_url = sheetsUrl;
      } else {
        toast.error('Please provide data to import');
        setLoading(false);
        return;
      }

      const res = await axios.post(`${API}/api/company/bulk-import/parse`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setParseResult(res.data);
      
      // Auto-select all new candidates
      const newIds = res.data.new_candidates?.map((c, i) => `new_${i}`) || [];
      setSelectedCandidates(new Set(newIds));
      
      if (res.data.errors?.length > 0) {
        toast.warning(`Parsed with ${res.data.errors.length} warnings`);
      } else {
        toast.success(`Found ${res.data.unique_count} unique emails`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!parseResult?.new_candidates?.length) {
      toast.error('No new candidates to save');
      return;
    }

    setLoading(true);
    try {
      const candidatesToSave = parseResult.new_candidates.filter((_, i) => 
        selectedCandidates.has(`new_${i}`)
      );

      const res = await axios.post(
        `${API}/api/company/bulk-import/save`,
        candidatesToSave,
        { headers: { Authorization: `Bearer ${token}` }}
      );

      toast.success(`Saved ${res.data.saved_count} candidates`);
      
      if (onComplete) {
        onComplete(res.data);
      }
      
      // Reset form
      setParseResult(null);
      setPasteEmails('');
      setCsvFile(null);
      setSheetsUrl('');
      setSelectedCandidates(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file');
        return;
      }
      setCsvFile(file);
    }
  }, []);

  const toggleSelect = (id) => {
    const newSet = new Set(selectedCandidates);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedCandidates(newSet);
  };

  const selectAll = () => {
    const allIds = parseResult?.new_candidates?.map((_, i) => `new_${i}`) || [];
    setSelectedCandidates(new Set(allIds));
  };

  const deselectAll = () => {
    setSelectedCandidates(new Set());
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Users className="text-purple-600" size={20} />
          Bulk Email Import
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Import candidate emails from various sources
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('paste')}
          className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
            activeTab === 'paste' 
              ? 'border-purple-600 text-purple-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Mail size={16} />
          Paste Emails
        </button>
        <button
          onClick={() => setActiveTab('csv')}
          className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
            activeTab === 'csv' 
              ? 'border-purple-600 text-purple-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileSpreadsheet size={16} />
          Upload CSV
        </button>
        <button
          onClick={() => setActiveTab('sheets')}
          className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
            activeTab === 'sheets' 
              ? 'border-purple-600 text-purple-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Link2 size={16} />
          Google Sheets
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Paste Emails Tab */}
        {activeTab === 'paste' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Paste emails (one per line or comma-separated)
              </label>
              <textarea
                value={pasteEmails}
                onChange={(e) => setPasteEmails(e.target.value)}
                placeholder="john@example.com&#10;jane@example.com&#10;bob@company.com"
                rows={8}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
              />
            </div>
          </div>
        )}

        {/* CSV Upload Tab */}
        {activeTab === 'csv' && (
          <div className="space-y-4">
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                csvFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-purple-400'
              }`}
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                {csvFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="text-green-600" size={24} />
                    <span className="text-green-700 font-medium">{csvFile.name}</span>
                    <button
                      onClick={(e) => { e.preventDefault(); setCsvFile(null); }}
                      className="ml-2 p-1 hover:bg-green-200 rounded"
                    >
                      <X size={16} className="text-green-700" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                    <p className="text-gray-600">Click to upload CSV file</p>
                    <p className="text-sm text-gray-400 mt-1">or drag and drop</p>
                  </>
                )}
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Column Name
                </label>
                <input
                  type="text"
                  value={emailColumn}
                  onChange={(e) => setEmailColumn(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name Column (optional)
                </label>
                <input
                  type="text"
                  value={nameColumn}
                  onChange={(e) => setNameColumn(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="name"
                />
              </div>
            </div>
          </div>
        )}

        {/* Google Sheets Tab */}
        {activeTab === 'sheets' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Sheets URL (must be public)
              </label>
              <input
                type="url"
                value={sheetsUrl}
                onChange={(e) => setSheetsUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-2">
                Make sure the sheet is shared as "Anyone with the link can view"
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Column Name
                </label>
                <input
                  type="text"
                  value={emailColumn}
                  onChange={(e) => setEmailColumn(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name Column (optional)
                </label>
                <input
                  type="text"
                  value={nameColumn}
                  onChange={(e) => setNameColumn(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="name"
                />
              </div>
            </div>
          </div>
        )}

        {/* Parse Button */}
        <button
          onClick={handleParse}
          disabled={loading}
          className="w-full mt-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Search size={18} />
              Parse & Preview
            </>
          )}
        </button>
      </div>

      {/* Parse Results */}
      {parseResult && (
        <div className="border-t border-gray-200">
          {/* Summary */}
          <div className="p-4 bg-gray-50 grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{parseResult.total_parsed}</p>
              <p className="text-xs text-gray-500">Total Parsed</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{parseResult.new_candidates?.length || 0}</p>
              <p className="text-xs text-gray-500">New</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{parseResult.existing_candidates?.length || 0}</p>
              <p className="text-xs text-gray-500">Already Exist</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{parseResult.errors?.length || 0}</p>
              <p className="text-xs text-gray-500">Errors</p>
            </div>
          </div>

          {/* Candidates List */}
          {parseResult.new_candidates?.length > 0 && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">New Candidates ({parseResult.new_candidates.length})</h4>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-xs text-purple-600 hover:underline">
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button onClick={deselectAll} className="text-xs text-gray-500 hover:underline">
                    Deselect All
                  </button>
                </div>
              </div>
              
              <div className="max-h-64 overflow-y-auto space-y-2">
                {parseResult.new_candidates.map((candidate, i) => (
                  <label
                    key={`new_${i}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedCandidates.has(`new_${i}`)
                        ? 'bg-purple-50 border-purple-300'
                        : 'bg-white border-gray-200 hover:border-purple-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCandidates.has(`new_${i}`)}
                      onChange={() => toggleSelect(`new_${i}`)}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{candidate.name}</p>
                      <p className="text-sm text-gray-500 truncate">{candidate.email}</p>
                    </div>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                      {candidate.source}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Errors */}
          {parseResult.errors?.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <h4 className="font-medium text-red-600 mb-2 flex items-center gap-2">
                <AlertCircle size={16} />
                Errors ({parseResult.errors.length})
              </h4>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {parseResult.errors.map((err, i) => (
                  <p key={i} className="text-sm text-red-600">
                    Row {err.row}: {err.email || ''} - {err.error}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={loading || selectedCandidates.size === 0}
              className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle size={18} />
                  Save {selectedCandidates.size} Candidates
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkEmailImport;
